import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Button, Chip, Skeleton, Stack } from "@mui/material";
import { type AdminMessage } from "@sendbird/chat/message";
import { useDeleteShiftInvite } from "@src/appV2/ShiftInvites/api/useDeleteShiftInvite";
import { PACIFIC_TIME_ZONE } from "@src/appV2/Timezone/constants";
import { isPast } from "date-fns";

import { APP_V2_APP_EVENTS, logError } from "../lib/analytics";
import { formatDurationBetweenDates } from "../lib/Time/formatDurationBetweenDates";
import { DESTRUCTIVE_BUTTON_SX } from "../redesign/components/Button.styles";
import { useGetShiftInvite } from "../ShiftInvites/api/useGetShiftInvite";
import { ChatWidgetCard } from "./ChatWidgetCard";
import {
  formatShiftStartEndDateTime,
  parseShiftInviteChatMessage,
  SHIFT_INVITE_STATUS_CHIP_MAP,
} from "./utils";

interface ShiftInviteRequestMessageProps {
  message: AdminMessage;
}

interface ShiftInviteRequestMessageContentProps {
  shiftInviteId: string;
}

export function ShiftInviteRequestMessage(props: ShiftInviteRequestMessageProps) {
  const { message } = props;

  const { shiftInviteId, status } = parseShiftInviteChatMessage(message);

  if (!isDefined(shiftInviteId) || !isDefined(status)) {
    logError(APP_V2_APP_EVENTS.SHIFT_INVITE_REQUEST_MESSAGE_RENDER_FAILURE, {
      error: new Error("Shift invite ID and status are required"),
      metadata: { shiftInviteId, status, messageId: message.messageId },
    });
    return null;
  }

  if (status === ShiftInviteStatus.DELETED) {
    return null;
  }

  return <ShiftInviteRequestMessageContent shiftInviteId={shiftInviteId} />;
}

export function ShiftInviteRequestMessageContent(props: ShiftInviteRequestMessageContentProps) {
  const { shiftInviteId } = props;

  const {
    data: shiftInviteResponse,
    isLoading,
    isError,
    error,
  } = useGetShiftInvite({ shiftInviteId });

  const { mutateAsync: deleteShiftInviteMutate, isLoading: isDeletingShiftInvite } =
    useDeleteShiftInvite();

  const shiftInvite = shiftInviteResponse?.data;

  if (isLoading) {
    return (
      <Stack alignItems="flex-end" className="sendbird-admin-message">
        <Skeleton variant="rounded" width={400} height={100} />
      </Stack>
    );
  }

  if (!isDefined(shiftInvite) || isError) {
    logError(APP_V2_APP_EVENTS.SHIFT_INVITE_REQUEST_MESSAGE_RENDER_FAILURE, {
      error: isError ? error : new Error("Shift invite data is undefined"),
      metadata: { shiftInviteId },
    });
    return null;
  }

  const { status, expiresAt, shiftDetails, facility } = shiftInvite.attributes;

  const isExpired =
    status === ShiftInviteStatus.EXPIRED ||
    (status === ShiftInviteStatus.PENDING && isPast(new Date(expiresAt)));

  const displayDuration = formatDurationBetweenDates({
    fromDate: new Date(),
    toDate: new Date(expiresAt),
    padMinutes: false,
  });

  const timeZone = facility.tmz ?? PACIFIC_TIME_ZONE;
  const formattedShiftTime = formatShiftStartEndDateTime(
    shiftDetails.start,
    shiftDetails.end,
    timeZone
  );

  const isActionable = status === ShiftInviteStatus.PENDING && !isExpired;

  const chipProps = isExpired
    ? SHIFT_INVITE_STATUS_CHIP_MAP[ShiftInviteStatus.EXPIRED]
    : SHIFT_INVITE_STATUS_CHIP_MAP[status];

  const handleRemove = async () => {
    await deleteShiftInviteMutate(shiftInviteId);
  };

  const removeButton = isActionable ? (
    <Button
      size="small"
      variant="outlined"
      sx={{
        ...DESTRUCTIVE_BUTTON_SX,
        flex: 1,
      }}
      disabled={isDeletingShiftInvite}
      onClick={handleRemove}
    >
      Remove
    </Button>
  ) : null;

  return (
    <Stack alignItems="flex-end" className="sendbird-admin-message">
      <ChatWidgetCard
        statusChip={
          <Chip
            label={chipProps.label}
            aria-label={chipProps.label}
            color={chipProps.color}
            variant="outlined"
            sx={{
              border: "none",
              borderRadius: 2,
              fontWeight: "bold",
              ...chipProps.customStyles,
            }}
          />
        }
        title="You sent a shift invite"
        subtitle={formattedShiftTime}
        actions={removeButton}
        caption={isActionable ? `Expiring in ${displayDuration}` : undefined}
      />
    </Stack>
  );
}
